import { passwordRequirementTooltip } from '@/components';
import { QueryKey, MAGIC_LINK_CHALLENGE } from '@/enums';
import { useParseQueryString } from '@/hooks';
import { useConfirmForgotPasswordMutation } from '@/hooks/useConfirmForgotPasswordMutation';
import { useInitForgotPasswordMutation } from '@/hooks/useInitForgotPasswordMutation';
import { TenantEntity } from '@/interfaces';
import { PUBLIC_ROUTES } from '@/routes';
import {
  INVALID_FIELDS,
  passwordPatternChecking,
  YupString,
} from '@/validations';
import {
  ControlledInput,
  CustomButton,
  CustomSpin,
} from '@amili-v3/amili-v3-design';
import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { get } from 'lodash';
import debounce from 'lodash/debounce';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AuthCardHeader from '../AuthCardHeader';
import { authPasswordToggle } from '../authPasswordToggle';
import { AuthFooterLink, AuthHelperText, AuthModal } from '../styles';
import { ResendCodeRow } from './styles';

const validationSchema = Yup.object().shape({
  password: YupString.matches(
    passwordPatternChecking,
    INVALID_FIELDS.PASSWORD_PATTERN,
  ),
  verificationCode: Yup.string()
    .required(INVALID_FIELDS.REQUIRED)
    .test('len', INVALID_FIELDS.PASSWORD_CODE, val => val.length === 6),
  verifyPassword: Yup.string()
    .oneOf([Yup.ref('password')], INVALID_FIELDS.PASSWORD_MATCH)
    .required(INVALID_FIELDS.REQUIRED),
});

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qsParams = useParseQueryString<{ email: string }>();

  const [open, setOpen] = useState(true);

  const queryClient = useQueryClient();

  const mutationResetPassword = useConfirmForgotPasswordMutation({
    onSuccess: () => {
      notification.success({ message: t('common.success') });
      setOpen(false);
      setTimeout(() => {
        navigate(PUBLIC_ROUTES.SIGN_IN);
      }, 200);
    },
  });

  const tenantData = queryClient.getQueryData([QueryKey.SUB_DOMAIN]);
  const tenant = get(tenantData, 'data', {}) as TenantEntity;
  const { id: tenantId } = tenant;

  const handleSubmit = (payload: Record<string, any>) => {
    if (!qsParams.email) return;
    const { password, verificationCode } = payload;

    return mutationResetPassword.mutateAsync({
      password,
      verificationCode,
      email: qsParams.email,
      tenantId,
    });
  };

  const mutationResendEmail = useInitForgotPasswordMutation({
    onSuccess: ({ data }) => {
      const { challenge } = data;
      if (challenge === MAGIC_LINK_CHALLENGE) {
        return navigate(PUBLIC_ROUTES.SIGN_IN);
      }
      notification.success({ message: t('authentication.resetLinkSent') });
    },
  });

  const handleResend = debounce(() => {
    if (!qsParams.email) return;
    return mutationResendEmail.mutateAsync({ email: qsParams.email, tenantId });
  }, 300);

  return (
    <AuthModal
      centered
      open={open}
      mask={false}
      width={420}
      hideCloseButton
      layout="vertical"
      footerBorder={false}
      title={<AuthCardHeader heading={t('authentication.resetPassword')} />}
      goBack={() => {
        setOpen(false);
        setTimeout(() => {
          navigate(PUBLIC_ROUTES.FORGOT_PASSWORD);
        }, 200);
      }}
      formikProps={{
        validationSchema,
        initialValues: {
          password: '',
          verifyPassword: '',
          verificationCode: '',
        },
        onSubmit: handleSubmit,
      }}
      footer={[
        <CustomButton
          size="large"
          key="submit"
          form="modal-id"
          htmlType="submit"
          aria-label="Reset btn"
          loading={mutationResetPassword.isPending}
        >
          {t('common.reset')}
        </CustomButton>,
      ]}
    >
      <AuthHelperText>{t('authentication.resetPasswordHelp')}</AuthHelperText>
      <ControlledInput
        required
        allowClear
        inputType="number"
        name="verificationCode"
        label={t('authentication.confirmationCode')}
        aria-label="confirmationCode inp"
      />
      <ControlledInput
        required
        name="password"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        label={t('common.password')}
        aria-label="password ipt"
        role="textbox"
        customFormProps={{ tooltip: passwordRequirementTooltip }}
      />
      <ControlledInput
        required
        inputType="password"
        passwordIconRender={authPasswordToggle}
        name="verifyPassword"
        label={t('authentication.confirmPassword')}
        aria-label="confirm password ipt"
        role="textbox"
      />
      <CustomSpin spinning={mutationResendEmail.isPending}>
        <ResendCodeRow>
          {t('authentication.doNotReceiveCode')}
          <AuthFooterLink
            href=" "
            aria-label="resend code link"
            onClick={e => {
              e.preventDefault();
              handleResend();
            }}
          >
            {t('authentication.resendCode')}
          </AuthFooterLink>
        </ResendCodeRow>
      </CustomSpin>
    </AuthModal>
  );
};

export default ResetPassword;
